import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, DialogContent, DialogTitle,
    FormControl, FormHelperText, FormLabel, IconButton, Input, Modal, ModalClose, ModalDialog,
    Option, Select, Stack, Switch, Textarea, Tooltip, Typography,
} from '@mui/joy';
import { Add, CardGiftcard, Delete, Edit, PlayCircleOutline, Stars } from '@mui/icons-material';
import { PageStandard } from '../utils/PageStandard';
import { Experience, RewardRuleModel, RewardRuleScope, RewardRuleType, StickerModel } from '../utils/Model';
import {
    createRewardRule, deleteRewardRule, getExperience, getExperienceRewardRules, getStickers,
    runExperienceRewardAssignment, updateRewardRule,
} from '../utils/ViewModel';

type ExpiryMode = 'none' | 'fixed' | 'days';

interface FormState {
    reward_type: RewardRuleType;
    scope: RewardRuleScope;
    rank: string;
    sticker_id: string;
    award_title: string;
    award_description: string;
    expiryMode: ExpiryMode;
    award_valid_until: string;
    award_valid_days: string;
    is_active: boolean;
}

const emptyForm: FormState = {
    reward_type: 'STICKER',
    scope: 'INDIVIDUAL_RANK',
    rank: '1',
    sticker_id: '',
    award_title: '',
    award_description: '',
    expiryMode: 'none',
    award_valid_until: '',
    award_valid_days: '30',
    is_active: true,
};

const scopeLabel = (scope: RewardRuleScope, rank: number | null) => {
    if (scope === 'ALL_PARTICIPANTS') return 'Tutti i partecipanti';
    const pos = rank ? `${rank}°` : '?';
    if (scope === 'INDIVIDUAL_RANK') return `${pos} classificato (individuale)`;
    return `Membri del gruppo ${pos} classificato`;
};

export function ExperienceRewards() {
    const { id: experienceId } = useParams<{ id: string }>();
    const [experience, setExperience] = useState<Experience | null>(null);
    const [rules, setRules] = useState<RewardRuleModel[]>([]);
    const [stickers, setStickers] = useState<StickerModel[]>([]);
    const [rewardsAssignedAt, setRewardsAssignedAt] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    // null = closed, undefined = create, RewardRuleModel = edit
    const [modal, setModal] = useState<RewardRuleModel | null | undefined>(null);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => { load(); }, [experienceId]);

    async function load() {
        if (!experienceId) return;
        setLoading(true);
        const [expRes, rulesRes, stickersRes] = await Promise.all([
            getExperience(experienceId),
            getExperienceRewardRules(experienceId),
            getStickers(),
        ]);
        if (expRes) setExperience(expRes.experience);
        setRules(rulesRes.rules);
        setRewardsAssignedAt(rulesRes.rewardsAssignedAt);
        setStickers(stickersRes);
        setLoading(false);
    }

    function openCreate() {
        setForm(emptyForm);
        setModal(undefined);
    }

    function openEdit(r: RewardRuleModel) {
        setForm({
            reward_type: r.reward_type,
            scope: r.scope,
            rank: r.rank != null ? String(r.rank) : '1',
            sticker_id: r.sticker_id ?? '',
            award_title: r.award_title ?? '',
            award_description: r.award_description ?? '',
            expiryMode: r.award_valid_days != null ? 'days' : r.award_valid_until ? 'fixed' : 'none',
            award_valid_until: r.award_valid_until ? r.award_valid_until.slice(0, 10) : '',
            award_valid_days: r.award_valid_days != null ? String(r.award_valid_days) : '30',
            is_active: r.is_active,
        });
        setModal(r);
    }

    function closeModal() { setModal(null); }

    const isCreate = modal === undefined;

    async function handleSave() {
        if (!experienceId) return;
        if (form.scope !== 'ALL_PARTICIPANTS' && (!form.rank || Number(form.rank) < 1)) {
            alert('Indica una posizione in classifica valida (>= 1)');
            return;
        }
        if (form.reward_type === 'STICKER' && !form.sticker_id) {
            alert('Seleziona uno sticker');
            return;
        }
        if (form.reward_type === 'AWARD' && !form.award_title.trim()) {
            alert('Indica il titolo del premio');
            return;
        }

        const input = {
            reward_type: form.reward_type,
            scope: form.scope,
            rank: form.scope === 'ALL_PARTICIPANTS' ? null : Number(form.rank),
            sticker_id: form.reward_type === 'STICKER' ? form.sticker_id : null,
            award_title: form.reward_type === 'AWARD' ? form.award_title.trim() : undefined,
            award_description: form.reward_type === 'AWARD' ? form.award_description.trim() : undefined,
            award_valid_until: form.reward_type === 'AWARD' && form.expiryMode === 'fixed' ? form.award_valid_until : null,
            award_valid_days: form.reward_type === 'AWARD' && form.expiryMode === 'days' ? Number(form.award_valid_days) : null,
            is_active: form.is_active,
        };

        setSaving(true);
        const ok = isCreate
            ? await createRewardRule(experienceId, input)
            : await updateRewardRule(modal!.id, input);
        setSaving(false);
        if (ok) { closeModal(); await load(); }
    }

    async function handleDelete(id: string) {
        if (!window.confirm('Eliminare questa regola?')) return;
        if (await deleteRewardRule(id)) await load();
    }

    async function handleToggleActive(r: RewardRuleModel) {
        await updateRewardRule(r.id, {
            reward_type: r.reward_type,
            scope: r.scope,
            rank: r.rank,
            sticker_id: r.sticker_id,
            award_title: r.award_title ?? undefined,
            award_description: r.award_description ?? undefined,
            award_valid_until: r.award_valid_until,
            award_valid_days: r.award_valid_days,
            is_active: !r.is_active,
        });
        await load();
    }

    async function handleRunNow() {
        if (!experienceId) return;
        const force = !!rewardsAssignedAt;
        const msg = force
            ? 'I premi sono già stati assegnati per questa esperienza. Rieseguire ora ne creerà di nuovi (gli sticker non si duplicano, i premi sì). Continuare?'
            : 'Eseguire subito le regole di assegnazione premi/sticker per tutti i partecipanti?';
        if (!window.confirm(msg)) return;

        setRunning(true);
        const result = await runExperienceRewardAssignment(experienceId, force);
        setRunning(false);
        if (result?.ran) {
            alert(`Fatto: ${result.rulesApplied ?? 0} regole applicate`);
            await load();
        }
    }

    return (
        <PageStandard
            title={`Premi & Sticker — ${experience?.title || experienceId}`}
            sidebarVisible
            headerEnd={
                <Stack direction="row" spacing={1.5} alignItems="center">
                    {rewardsAssignedAt && (
                        <Chip size="sm" color="success" variant="soft">
                            Assegnati il {new Date(rewardsAssignedAt).toLocaleString()}
                        </Chip>
                    )}
                    <Button
                        variant="outlined"
                        color="neutral"
                        startDecorator={<PlayCircleOutline />}
                        loading={running}
                        onClick={handleRunNow}
                    >
                        Esegui ora
                    </Button>
                    <Button startDecorator={<Add />} onClick={openCreate}>
                        Nuova regola
                    </Button>
                </Stack>
            }
        >
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Le regole vengono applicate automaticamente non appena tutti i capigruppo dell'esperienza
                raggiungono la fine del proprio flow. Puoi anche eseguirle manualmente col pulsante "Esegui ora".
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : rules.length === 0 ? (
                <Typography level="body-md" color="neutral" sx={{ mt: 4, textAlign: 'center' }}>
                    Nessuna regola configurata. Creane una col pulsante in alto.
                </Typography>
            ) : (
                <Stack spacing={1.5}>
                    {rules.map((r) => (
                        <Card key={r.id} variant="outlined" sx={{ opacity: r.is_active ? 1 : 0.55 }}>
                            <CardContent orientation="horizontal" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                {r.reward_type === 'STICKER' ? (
                                    <CardGiftcard sx={{ color: 'primary.500' }} />
                                ) : (
                                    <Stars sx={{ color: 'warning.500' }} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="title-sm">
                                        {r.reward_type === 'STICKER'
                                            ? `Sticker: ${r.sticker_title || r.sticker_id}`
                                            : `Premio: ${r.award_title}`}
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                        {scopeLabel(r.scope, r.rank)}
                                        {r.reward_type === 'AWARD' && r.award_valid_days != null &&
                                            ` · scade ${r.award_valid_days} giorni dopo l'assegnazione`}
                                        {r.reward_type === 'AWARD' && r.award_valid_until && r.award_valid_days == null &&
                                            ` · scade il ${new Date(r.award_valid_until).toLocaleDateString()}`}
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={r.is_active}
                                    onChange={() => handleToggleActive(r)}
                                    endDecorator={r.is_active ? 'Attiva' : 'Disattiva'}
                                />
                                <Tooltip title="Modifica">
                                    <IconButton size="sm" variant="soft" onClick={() => openEdit(r)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Elimina">
                                    <IconButton size="sm" variant="soft" color="danger" onClick={() => handleDelete(r.id)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}

            {/* Create / Edit modal */}
            <Modal open={modal !== null} onClose={closeModal}>
                <ModalDialog minWidth={460}>
                    <ModalClose />
                    <DialogTitle>{isCreate ? 'Nuova regola' : 'Modifica regola'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <FormControl>
                                <FormLabel>Tipo di ricompensa</FormLabel>
                                <Select
                                    value={form.reward_type}
                                    onChange={(_, v) => v && setForm(f => ({ ...f, reward_type: v as RewardRuleType }))}
                                >
                                    <Option value="STICKER">Sticker</Option>
                                    <Option value="AWARD">Premio (coupon)</Option>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>A chi assegnarla</FormLabel>
                                <Select
                                    value={form.scope}
                                    onChange={(_, v) => v && setForm(f => ({ ...f, scope: v as RewardRuleScope }))}
                                >
                                    <Option value="INDIVIDUAL_RANK">N° posizione in classifica individuale</Option>
                                    <Option value="GROUP_RANK">Tutti i membri del gruppo N° in classifica</Option>
                                    <Option value="ALL_PARTICIPANTS">Tutti i partecipanti</Option>
                                </Select>
                            </FormControl>

                            {form.scope !== 'ALL_PARTICIPANTS' && (
                                <FormControl required>
                                    <FormLabel>Posizione in classifica</FormLabel>
                                    <Input
                                        type="number"
                                        slotProps={{ input: { min: 1 } }}
                                        value={form.rank}
                                        onChange={e => setForm(f => ({ ...f, rank: e.target.value }))}
                                    />
                                    <FormHelperText>1 = primo classificato, 2 = secondo, ecc.</FormHelperText>
                                </FormControl>
                            )}

                            {form.reward_type === 'STICKER' ? (
                                <FormControl required>
                                    <FormLabel>Sticker</FormLabel>
                                    <Select
                                        value={form.sticker_id || null}
                                        placeholder="Seleziona uno sticker…"
                                        onChange={(_, v) => setForm(f => ({ ...f, sticker_id: (v as string) || '' }))}
                                    >
                                        {stickers.map(s => (
                                            <Option key={s.id} value={s.id}>{s.title}</Option>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <>
                                    <FormControl required>
                                        <FormLabel>Titolo premio</FormLabel>
                                        <Input
                                            value={form.award_title}
                                            onChange={e => setForm(f => ({ ...f, award_title: e.target.value }))}
                                            placeholder="es. Sconto 20% presso La Pergola"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Descrizione</FormLabel>
                                        <Textarea
                                            minRows={2}
                                            value={form.award_description}
                                            onChange={e => setForm(f => ({ ...f, award_description: e.target.value }))}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Scadenza</FormLabel>
                                        <Select
                                            value={form.expiryMode}
                                            onChange={(_, v) => v && setForm(f => ({ ...f, expiryMode: v as ExpiryMode }))}
                                        >
                                            <Option value="none">Nessuna scadenza</Option>
                                            <Option value="days">N giorni dopo l'assegnazione</Option>
                                            <Option value="fixed">Data fissa</Option>
                                        </Select>
                                    </FormControl>
                                    {form.expiryMode === 'days' && (
                                        <FormControl>
                                            <FormLabel>Giorni di validità</FormLabel>
                                            <Input
                                                type="number"
                                                slotProps={{ input: { min: 1 } }}
                                                value={form.award_valid_days}
                                                onChange={e => setForm(f => ({ ...f, award_valid_days: e.target.value }))}
                                            />
                                        </FormControl>
                                    )}
                                    {form.expiryMode === 'fixed' && (
                                        <FormControl>
                                            <FormLabel>Data di scadenza</FormLabel>
                                            <Input
                                                type="date"
                                                value={form.award_valid_until}
                                                onChange={e => setForm(f => ({ ...f, award_valid_until: e.target.value }))}
                                            />
                                        </FormControl>
                                    )}
                                </>
                            )}

                            <Switch
                                checked={form.is_active}
                                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                                endDecorator="Regola attiva"
                            />

                            <Button loading={saving} onClick={handleSave}>
                                {isCreate ? 'Crea regola' : 'Salva'}
                            </Button>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </PageStandard>
    );
}
